/** The canvas stage: tools, frames on the canvas, their menus, comments and
 *  the drawing, path and resize furniture. */
export const stage = {
  /* the tool rail */
  canvasTools: 'Canvas tools',
  toolTooltip: '{label} ({key})',
  canvasMode: 'Canvas mode',
  modeEdit: 'Edit mode',
  modePrototype: 'Prototype mode: wire up interactions',
  modeComment: 'Comment mode (C)',
  hintPath: 'Click for corners · Drag for curves · Snap to points · Enter to finish',
  hintText: 'Click or drag, then type · {mod}Enter to save',
  hintCircle: 'Drag to draw a circle · Esc to cancel',
  hintShape: 'Drag to draw · Shift for a square · Esc to cancel',
  finishPath: 'Finish path',
  newFrame: 'New frame',

  /* drawing, path editing, moving and resizing */
  textInputLabel: 'Text to add to canvas',
  textPlaceholder: 'Type something…',
  pathHandleIncoming: 'Point {n} incoming handle',
  pathHandleOutgoing: 'Point {n} outgoing handle',
  pathPoint: 'Path point {n}',
  savingPath: 'Saving path…',
  pathHint: 'Drag points or handles · click the outline to add a point · Alt to release snapping',
  makeCorner: 'Make corner',
  makeCurve: 'Make curve',
  deletePoint: 'Delete point',
  done: 'Done',
  movingDrawing: 'Moving drawing',
  resize: 'Resize',
  resizeWidth: 'Resize width',
  resizeHeight: 'Resize height',
  rulerHorizontal: 'Horizontal ruler: drag down to add a guide',
  rulerVertical: 'Vertical ruler: drag right to add a guide',
  guideRemove: 'Remove',

  /* a frame on the canvas */
  moveElementFailed: 'Couldn’t move the element',
  elementGone: 'This element no longer exists. Showing its frame instead.',
  lockedTip: 'Locked — unlock it from the Layers panel or the Inspector',
  syncedTip: 'Synced from a live app',
  githubPlaceholderTip: 'Found in the repo — awaiting capture',
  githubImportedTip: 'Imported from a GitHub repo',
  isDesigning: '{name} is designing',
  isOnIt: '{name} is on it',
  elementComment: 'Comment',
  askAi: 'Ask AI',
  code: 'Code',
  editText: 'Edit text',
  textFormatting: 'Text formatting',
  textBold: 'Bold',
  textItalic: 'Italic',
  textUnderline: 'Underline',
  textStrikethrough: 'Strikethrough',
  textBulletList: 'Bulleted list',
  textNumberedList: 'Numbered list',
  frameChanged: 'Frame changed. Right-click again.',
  findingElement: 'Finding element…',

  /* the frame and element menus */
  paste: 'Paste',
  copyClipboardFailed: 'Couldn’t copy to the clipboard. Please try again.',
  duplicate: 'Duplicate',
  show: 'Show',
  hide: 'Hide',
  lock: 'Lock',
  unlock: 'Unlock',
  copyHtml: 'Copy HTML',
  copySelector: 'Copy selector',
  copyLink: 'Copy link',
  copyLinkToElement: 'Copy link to element',
  moveUp: 'Move up',
  moveDown: 'Move down',
  deleteElement: 'Delete element',
  deleteFrame: 'Delete frame',
  copyFrames: 'Copy {count} frames',
  duplicateFrames: 'Duplicate {count} frames',
  showFrames: 'Show {count} frames',
  hideFrames: 'Hide {count} frames',
  lockFrames: 'Lock {count} frames',
  unlockFrames: 'Unlock {count} frames',
  deleteFrames: 'Delete {count} frames',
  framesCount: '{count} frames',
  copyAsPng: 'Copy as PNG',
  copyImageUrl: 'Copy image URL',
  imageUrlCopied: 'Image URL copied',
  copyImageUrlFailed: 'Couldn’t copy the image URL',
  exportEllipsis: 'Export…',
  versionHistory: 'Version history…',
  markReadyForDev: 'Mark ready for dev',
  moveToDraft: 'Move to draft',

  /* approval */
  readyForDev: 'Ready for dev',
  needsReview: 'Needs review',
  approvedByOn: 'Approved by {name} on {date}',
  previouslyApprovedByOn: 'Previously approved by {name} on {date}',
  markAsApproved: 'Mark as approved',
  approvalReady: 'Agents can see this screen is approved for implementation.',
  approvalChanged: 'This design changed after approval. Review it before marking it ready again.',
  approvalPrompt: 'Approve this screen to let your team and agents know it’s ready to build.',
  saving: 'Saving…',
  approvalWait: 'Wait for changes to finish saving before approving.',

  /* comments */
  commentOnElement: 'Leave a comment on this element…',
  commentOnFrame: 'Leave a comment on this frame…',
  commentHere: 'Leave a comment here…',
  pinReplies: { one: ' ({count} reply)', other: ' ({count} replies)' },
  willPickUp: '{name} will pick this up',
  post: 'Post',
  agentStopped: '{name} stopped',
  waitingFor: 'waiting for {name}',
  agentDidNotFinish: 'The agent did not finish.',
  retry: 'Retry',
  replyAskAgent: 'Reply… (@doop to ask the agent)',
  replyEllipsis: 'Reply…',
  replyFailed: 'That reply did not go through — it may be resolved already. Try again.',
  resolve: 'Resolve',
  posting: 'Posting…',
  reply: 'Reply',
  noComments: 'No comments on this page yet. Use the comment tool or right-click an element to leave one.',
  commentFilter: 'Comment filter',
  filterOpen: 'Open',
  filterResolved: 'Resolved',
  noOpenThreads: 'No open threads on this page.',
  noResolvedThreads: 'No resolved threads on this page.',
  goToFrame: 'Go to “{name}”',
  goToPin: 'Go to the pin on the canvas',
  resolvedBy: 'resolved by {name}',
  replies: { one: '{count} reply', other: '{count} replies' },
  resolvedReplies: 'Resolved replies',
  replyPanelPlaceholder: 'Reply… @doop to ask the agent',
  replyTo: 'Reply to {name}',
  markResolved: 'Mark this thread as resolved',
} as const
